import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
// Animations
import { slideInDownAnimation } from '../../app/app.animation';
// Common
import { AngularMaterialService, ErrorParseService, IFormConfiguration, LGEDataService, PermissionsService } from '../../commons';
// State
import { Store } from '@ngrx/store';
import * as lgeApp from '../../app/state/app.actions';
import * as fromFormConfiguration from './state/form-configuration.selectors';
import * as fromAuth from '../login/state/login.selectors';
import * as FormConfigurationAction from './state/form-configuration.actions';
import * as _ from 'lodash';
import { Observable, skipWhile, Subscription } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import moment from 'moment';


@Component({
    selector: 'form-configuration-screen',
    templateUrl: 'form-configuration.component.html',
    styleUrls: ['form-configuration.scss'],
    animations: [slideInDownAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormConfigurationScreenComponent implements OnInit, OnDestroy {
    formConfig: FormGroup;
    minEffdt = new Date('');
    @Input() pending$: Observable<boolean>;
    pendingSubscription: Subscription;
    pending: boolean;
    @Input() error$: Observable<string | null>;
    @Input() selectedIndex: number;
    errorSubscription: Subscription;
    error: string | null;
    @Input() stateData$: Observable<IFormConfiguration[] | null>;
    stateDataSubscription: Subscription;
    stateData: IFormConfiguration | null;

    isLoadingResults = false;
    showDetail = false;
    showErrorMessage: string;
    formConfigList = [];
    formName = '';
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild('tabGroup') tabGroup;

    constructor(
        private store: Store<fromFormConfiguration.State>,
        private ref: ChangeDetectorRef,
        private fb: FormBuilder,
        private ams: AngularMaterialService,
        private permissionsService: PermissionsService,
        private errorParse: ErrorParseService,
        private _dataService: LGEDataService
    ) {
        this.selectState();
        this.createForms();
    }

    ngOnInit() {
        this.startSubscriptions();
    }

    hasRole(role: string) {
        return this.permissionsService.hasRoles(role);
    }

    GetFormConfigs() {
        this.isLoadingResults = true;
        this._dataService.GetFormConfigs().subscribe({
            next: data => {
                this.ams.closeDialog();
                this.formConfigList = data;
                this.ref.markForCheck();
            },
            error: error => {
                this.ams.closeDialog();
                this.showErrorMessage = 'Resource not available!';
                this.ams.showAlert('Error!', 'Resource not available!');
                this.ref.markForCheck();
            }
        })
        this.ref.markForCheck();
    }

    openFormConfig() {
        let name = 'PP';
        switch (this.tabGroup?.selectedIndex) {
            case 1:
                name = 'CG';
                break;
            case 2:
                name = 'ES';
                break;
            case 3:
                name = 'CI';
                break;
            case 4:
                name = 'FO';
                break;
            default:
                name = 'PP';
                break;
        }
        if (this.formConfig.dirty && name !== this.formConfig.value.name
            && this.formConfigList.find((data) => data.name === this.formConfig.value.name) !== undefined) {
            this.ams.showConfirm('Unsaved changes.', ' Do you want to save?');
            this.ams.confirm.afterClosed().subscribe(result => {
                if (result !== "no") {
                    this.onSave();
                }
                this.loadFormConfig(name);
                this.showDetail = true;
            });
        } else {
            this.loadFormConfig(name);
            this.showDetail = true;
        }
    }
    loadFormConfig(name: string) {
        switch (name) {
            case 'PP':
                this.formName = 'Plant for the Planet';
                break;
            case 'FO':
                this.formName = 'LG&E-KU Foundation';
                break;
            case 'CI':
                this.formName = 'Community Investment Funding';
                break;
            case 'CG':
                this.formName = 'Community Grants';
                break;
            case 'ES':
                this.formName = 'Event Sponsorship';
                break;
        }
        const obj = _.find(this.formConfigList, (item) => item.name === name);
        this.formConfig.reset();
        if (obj !== undefined) {
            this.formConfig.get('name').setValue(obj.name);
            this.formConfig.get('startDate').setValue(new Date(obj.startDate));
            this.formConfig.get('endDate').setValue(new Date(obj.endDate));
        } else {
            this.formConfig.get('name').setValue(name);
            this.formConfig.get('startDate').setValue(new Date());
            this.formConfig.get('endDate').setValue(new Date());
            this.formConfig.markAsDirty();
        }
    }

    onSave() {
        const startString = moment(this.formConfig.value.startDate.toString()).format('MM/DD/YYYY');
        const endString = moment(this.formConfig.value.endDate.toString()).format('MM/DD/YYYY');
        this.formConfig.get('startDate').setValue(startString);
        this.formConfig.get('endDate').setValue(endString);
        this.store.dispatch(new FormConfigurationAction.SaveFormConfiguration(this.formConfig.value));
    }

    onCancel() {
        this.formConfig.reset();
    }

    // Select state
    selectState(): void {
        this.pending$ = this.store.select(fromFormConfiguration.getFormConfigurationScreenPending);
        this.stateData$ = this.store.select(fromAuth.getFormConfigs).pipe(skipWhile(configs => configs === undefined || configs === null));
        this.error$ = this.store.select(fromFormConfiguration.getFormConfigurationScreenError).pipe(skipWhile(error => error === undefined || error === null || error === ''));
    }

    displayLoader(pending: boolean): void {
        if (pending) {
            setTimeout(() => {
                this.ams.showLoader('Loading...');
                this.ref.markForCheck();
            })
        } else {
            this.ams.closeDialog().then(() => {
                if (this.error !== null && this.error !== undefined && this.error !== '') {
                    this.displayError(this.error);
                }
            });
        }
    }

    displayError(error: any): void {
        let errorMessage;
        if (this.errorParse.foundKeyWord(error, 'CORPCOMMONWS')) {
            errorMessage = this.errorParse.translateCCError(error);
        } else {
            errorMessage = error instanceof Object ? error.message : error;
        }
        this.ams.showAlert('Loading Error', errorMessage);
        this.ams.alert.afterClosed().subscribe(result => {
            this.store.dispatch(new FormConfigurationAction.FormConfigurationScreenErrorReset());
        });
    }

    // Create reactive forms
    createForms(): void {
        this.formConfig =
            this.fb.group({
                name: ['', [Validators.required]],
                startDate: [moment(), [Validators.required]],
                endDate: [moment(), [Validators.required]]
            });
        this.formConfig.get('startDate').valueChanges.subscribe((newVal) => {
            const endDate = this.formConfig.value.endDate;
            if (newVal !== null && endDate !== null) {
                if (new Date(newVal) > new Date(endDate)) {
                    this.ams.showAlert('Time Range Invalid', 'Please select valid start date!');
                    this.formConfig.get('startDate').setValue(endDate);
                }
            }
        });

        this.formConfig.get('endDate').valueChanges.subscribe((newVal) => {
            const startDate = this.formConfig.value.startDate;
            if (newVal !== null && startDate !== null) {
                if (new Date(startDate) > new Date(newVal)) {
                    this.ams.showAlert('Time Range Invalid', 'Please select valid end date!');
                    this.formConfig.get('endDate').setValue(startDate);
                }
            }
        });
    }

    // Opens sidenav if on mobile
    public openSidenav(): void {
        this.store.dispatch(new lgeApp.OpenSidenav());
    }

    // Subscribe to state data and mark it for changes
    startSubscriptions(): void {
        this.pendingSubscription = this.pending$
            .subscribe(data => {
                this.pending = data;
                this.displayLoader(this.pending);
                this.ref.markForCheck();
            });
        this.errorSubscription = this.error$
            .subscribe(data => {
                this.error = data;
                this.ref.markForCheck();
            });
        this.stateDataSubscription = this.stateData$
            .subscribe(data => {
                // this.stateData = data;
                this.formConfigList = JSON.parse(JSON.stringify(data));
                this.ref.markForCheck();
                this.openFormConfig();
            });

        this.GetFormConfigs();
        setTimeout(() => {
            this.ams.showLoader('Loading ...');
        });
    }

    // Unsubscribe from state data changes
    endSubscriptions(): void {
        this.pendingSubscription.unsubscribe();
        this.errorSubscription.unsubscribe();
        this.stateDataSubscription.unsubscribe();
    }

    ngOnDestroy() {
        this.endSubscriptions();
    }
}